import React, {useState, useEffect, useCallback} from 'react';
import Select, {components} from 'react-select';
import classnames from 'classnames';
import {ErrorMessage} from 'formik';
import _get from 'lodash/get';
import {Trans} from 'react-i18next';

export default function SelectBox({
  form: {setFieldValue, touched, errors},
  field: {name, value},
  options = [],
  isSearchable = false,
  isDisabled,
  onChange,
  showError = true,
  isOptionsChanged = false,
}) {
  const [selectedOption, setSelectedOption] = useState(null);
  const error = _get(touched, name) && _get(errors, name);
  useEffect(() => {
    /* find the default option object by the default value string */
    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      if (option.value === value) {
        setSelectedOption(option);
        return;
      }

      const opts = _get(option, 'options', []);
      for (let j = 0; j < opts.length; j++) {
        const opt = opts[j];
        if (opt.value === value) {
          setSelectedOption(opt);
          return;
        }
      }
    }
    setSelectedOption(null);
  }, [value]);

  useEffect(() => {
    if (!isOptionsChanged) {
      return;
    }
    const isValueValid = options.some(option => option.value === value);
    if (!isValueValid) {
      setFieldValue(name, null);
    }
  }, [options]);

  const handleChange = useCallback(
    option => {
      setSelectedOption(option);
      setFieldValue(name, option && option.value);
      onChange && onChange(option.value);
    },
    [name, setFieldValue, setSelectedOption]
  );

  const customControl = useCallback(
    ({children, ...props}) => (
      <>
        <components.Control
          {...props}
          className={classnames({
            'custom-react-select': true,
            'is-invalid': !!error,
          })}
        >
          {children}
        </components.Control>
        {showError && (
          <span className="invalid-feedback">
            <ErrorMessage name={name} />
          </span>
        )}
      </>
    ),
    [name, error]
  );

  const customStyles = useCallback(
    /* do not control the border color on hover if error */
    () => ({
      control: base => ({
        ...base,
        height: '45px',
        ...(error && {
          '&:hover': {
            borderColor: null,
          },
        }),
      }),
    }),
    [error]
  );

  return (
    <Select
      value={selectedOption}
      options={options}
      onChange={handleChange}
      isSearchable={isSearchable}
      components={{Control: customControl}}
      styles={customStyles()}
      isMulti={false}
      isDisabled={isDisabled}
      placeholder={<Trans i18nKey="c.c.select.placeholder">Select...</Trans>}
    />
  );
}
